import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBug, faCheckCircle, faTimesCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { getTokenInfo } from '../../../helpers/tokenHelper';
import { get } from '../../../helpers/apiHelper';
import useAuthStore from '../../../store/authStore';
import useUserRole from '../../../hooks/useUserRole';

const SecurityDiagnostics = () => {
    const [diagnostics, setDiagnostics] = useState(null);
    const [loading, setLoading] = useState(false);
    const { user } = useAuthStore();
    const { userRole, loading: roleLoading } = useUserRole();

    const runDiagnostics = async () => {
        setLoading(true);
        setDiagnostics(null);

        const results = {
            token: null,
            tokenInfo: null,
            userRole: null,
            endpointTest: null,
            timestamp: new Date().toISOString(),
        };

        try {
            // 1. Verificar token
            try {
                const storedData = localStorage.getItem('auth-storage-petitprime');
                if (storedData) {
                    const parsedData = JSON.parse(storedData);
                    results.token = parsedData?.state?.token ? 'Presente' : 'No encontrado';
                    results.tokenLength = parsedData?.state?.token?.length || 0;
                } else {
                    results.token = 'No encontrado';
                }
            } catch (err) {
                results.token = `Error: ${err.message}`;
            }

            // 2. Verificar información del token
            try {
                const tokenInfo = getTokenInfo();
                results.tokenInfo = tokenInfo;
                results.tokenRole = tokenInfo?.role || 'No encontrado en token';
            } catch (err) {
                results.tokenInfo = `Error: ${err.message}`;
            }

            // 2b. Obtener rol del usuario desde el endpoint (método correcto)
            try {
                if (user?.userId) {
                    const userData = await get(`users/${user.userId}`);
                    results.userRoleFromAPI = userData?.role || userData?.userRole || 'No encontrado';
                    results.userData = {
                        userId: userData?._id || userData?.id,
                        email: userData?.email,
                        role: userData?.role || userData?.userRole,
                    };
                } else {
                    results.userRoleFromAPI = 'No hay userId disponible';
                }
            } catch (err) {
                results.userRoleFromAPI = `Error: ${err.message}`;
            }

            // Usar el hook useUserRole como fuente de verdad
            results.userRoleFromHook = userRole || 'No disponible aún';

            // 3. Probar endpoint de IPs bloqueadas
            try {
                await get('admin/security/blocked-ips');
                results.endpointTest = 'Éxito';
            } catch (err) {
                results.endpointTest = {
                    status: err.status,
                    message: err.message,
                    details: err.details,
                };
            }

            // 4. Probar endpoint de estadísticas (con el problema de days)
            try {
                await get('admin/security/stats?days=7');
                results.statsEndpointTest = 'Éxito';
            } catch (err) {
                results.statsEndpointTest = {
                    status: err.status,
                    message: err.message,
                    details: err.details,
                    isDaysError: err.message && err.message.includes('days'),
                };
            }
        } catch (err) {
            console.error('Error en diagnóstico:', err);
        } finally {
            setDiagnostics(results);
            setLoading(false);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
                <FontAwesomeIcon icon={faBug} className="text-blue-600 text-xl" />
                <h3 className="text-lg font-semibold text-gray-900">Diagnóstico de Seguridad</h3>
            </div>

            <p className="text-sm text-gray-600 mb-4">
                Esta herramienta ayuda a identificar problemas de autenticación y autorización con los
                endpoints de seguridad.
            </p>

            <button
                onClick={runDiagnostics}
                disabled={loading}
                className="mb-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
                {loading ? 'Ejecutando diagnóstico...' : 'Ejecutar Diagnóstico'}
            </button>

            {diagnostics && (
                <div className="space-y-4 mt-6">
                    {/* Token Status */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <FontAwesomeIcon
                                icon={diagnostics.token === 'Presente' ? faCheckCircle : faTimesCircle}
                                className={diagnostics.token === 'Presente' ? 'text-green-600' : 'text-red-600'}
                            />
                            Token de Autenticación
                        </h4>
                        <div className="text-sm space-y-1 ml-6">
                            <p>
                                <strong>Estado:</strong> {diagnostics.token}
                            </p>
                            {diagnostics.tokenLength && (
                                <p>
                                    <strong>Longitud:</strong> {diagnostics.tokenLength} caracteres
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Token Info */}
                    {diagnostics.tokenInfo && (
                        <div className="border border-gray-200 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-2">Información del Token</h4>
                            <div className="text-sm space-y-1 ml-4">
                                <pre className="bg-gray-50 p-2 rounded text-xs overflow-x-auto">
                                    {JSON.stringify(diagnostics.tokenInfo, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}

                    {/* User Role */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <FontAwesomeIcon
                                icon={
                                    diagnostics.userRoleFromHook === 'ADMIN_ROLE' ||
                                        diagnostics.userRoleFromAPI === 'ADMIN_ROLE' ||
                                        diagnostics.userRoleFromAPI === 'admin'
                                        ? faCheckCircle
                                        : faTimesCircle
                                }
                                className={
                                    diagnostics.userRoleFromHook === 'ADMIN_ROLE' ||
                                        diagnostics.userRoleFromAPI === 'ADMIN_ROLE' ||
                                        diagnostics.userRoleFromAPI === 'admin'
                                        ? 'text-green-600'
                                        : 'text-red-600'
                                }
                            />
                            Rol de Usuario
                        </h4>
                        <div className="text-sm space-y-2 ml-6">
                            <div>
                                <p>
                                    <strong>Rol desde Hook (useUserRole):</strong>{' '}
                                    <span
                                        className={
                                            diagnostics.userRoleFromHook === 'ADMIN_ROLE'
                                                ? 'text-green-600 font-semibold'
                                                : 'text-gray-600'
                                        }
                                    >
                                        {diagnostics.userRoleFromHook}
                                    </span>
                                </p>
                            </div>
                            <div>
                                <p>
                                    <strong>Rol desde API (/users/{user?.userId}):</strong>{' '}
                                    <span
                                        className={
                                            diagnostics.userRoleFromAPI === 'ADMIN_ROLE' || diagnostics.userRoleFromAPI === 'admin'
                                                ? 'text-green-600 font-semibold'
                                                : 'text-gray-600'
                                        }
                                    >
                                        {diagnostics.userRoleFromAPI}
                                    </span>
                                </p>
                            </div>
                            <div>
                                <p>
                                    <strong>Rol en Token JWT:</strong>{' '}
                                    <span className="text-gray-600">
                                        {diagnostics.tokenRole} {diagnostics.tokenRole === 'No encontrado en token' && '(Normal - el rol no está en el token)'}
                                    </span>
                                </p>
                            </div>
                            {(diagnostics.userRoleFromHook !== 'ADMIN_ROLE' &&
                                diagnostics.userRoleFromAPI !== 'ADMIN_ROLE' &&
                                diagnostics.userRoleFromAPI !== 'admin') && (
                                    <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-md p-2">
                                        <p className="text-xs text-yellow-800">
                                            ⚠️ El rol debe ser <strong>ADMIN_ROLE</strong> o <strong>admin</strong> para
                                            acceder a los endpoints de seguridad.
                                        </p>
                                        <p className="text-xs text-yellow-800 mt-1">
                                            <strong>Nota:</strong> El rol no está en el token JWT, se obtiene del endpoint{' '}
                                            <code>/users/{user?.userId}</code>. Esto es normal y correcto.
                                        </p>
                                    </div>
                                )}
                        </div>
                    </div>

                    {/* Endpoint Test - Blocked IPs */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <FontAwesomeIcon
                                icon={diagnostics.endpointTest === 'Éxito' ? faCheckCircle : faTimesCircle}
                                className={
                                    diagnostics.endpointTest === 'Éxito' ? 'text-green-600' : 'text-red-600'
                                }
                            />
                            Prueba de Endpoint (IPs Bloqueadas)
                        </h4>
                        <div className="text-sm space-y-1 ml-6">
                            {diagnostics.endpointTest === 'Éxito' ? (
                                <p className="text-green-600">✅ El endpoint responde correctamente</p>
                            ) : (
                                <div>
                                    <p className="text-red-600 font-semibold mb-2">❌ Error al acceder al endpoint</p>
                                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                        <p>
                                            <strong>Status:</strong> {diagnostics.endpointTest?.status || 'N/A'}
                                        </p>
                                        <p>
                                            <strong>Mensaje:</strong> {diagnostics.endpointTest?.message || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Endpoint Test - Stats */}
                    {diagnostics.statsEndpointTest && (
                        <div className="border border-gray-200 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                <FontAwesomeIcon
                                    icon={diagnostics.statsEndpointTest === 'Éxito' ? faCheckCircle : faTimesCircle}
                                    className={
                                        diagnostics.statsEndpointTest === 'Éxito' ? 'text-green-600' : 'text-red-600'
                                    }
                                />
                                Prueba de Endpoint (Estadísticas)
                            </h4>
                            <div className="text-sm space-y-1 ml-6">
                                {diagnostics.statsEndpointTest === 'Éxito' ? (
                                    <p className="text-green-600">✅ El endpoint responde correctamente</p>
                                ) : (
                                    <div>
                                        <p className="text-red-600 font-semibold mb-2">❌ Error al acceder al endpoint</p>
                                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                            <p>
                                                <strong>Status:</strong> {diagnostics.statsEndpointTest?.status || 'N/A'}
                                            </p>
                                            <p>
                                                <strong>Mensaje:</strong> {diagnostics.statsEndpointTest?.message || 'N/A'}
                                            </p>
                                            {diagnostics.statsEndpointTest?.isDaysError && (
                                                <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-md p-2">
                                                    <p className="text-xs text-yellow-800 font-semibold mb-1">
                                                        ⚠️ Problema con el parámetro "days"
                                                    </p>
                                                    <p className="text-xs text-yellow-800">
                                                        El backend está recibiendo <code>days=7</code> como string, pero necesita
                                                        parsearlo como número. En NestJS, usa <code>ParseIntPipe</code> o{' '}
                                                        <code>@Type(() => Number)</code> en el DTO.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Recommendations */}
                    {diagnostics.endpointTest !== 'Éxito' && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start gap-2">
                                <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 mt-0.5" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-semibold mb-2">Posibles causas del error 403:</p>
                                    <ul className="list-disc list-inside space-y-1 ml-2">
                                        <li>
                                            <strong>Backend:</strong> Los endpoints no están implementados o no están
                                            protegidos correctamente con AdminGuard
                                        </li>
                                        <li>
                                            <strong>Backend:</strong> El AdminGuard está rechazando las peticiones porque
                                            el usuario no tiene el rol correcto
                                        </li>
                                        <li>
                                            <strong>Backend:</strong> El token no se está validando correctamente en el
                                            backend
                                        </li>
                                        <li>
                                            <strong>Backend:</strong> La ruta del endpoint no coincide con la
                                            configuración del backend
                                        </li>
                                    </ul>
                                    <p className="mt-3 font-semibold">Solución:</p>
                                    <p>
                                        Verifica en el backend que los endpoints estén implementados y protegidos
                                        correctamente con AdminGuard. El rol del usuario debe ser{' '}
                                        <strong>ADMIN_ROLE</strong> o <strong>admin</strong>.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SecurityDiagnostics;

